import React, { useState } from "react";
import styled from "styled-components";
import {
  Modal,
  Form,
  Select,
  DatePicker,
  Button,
  Upload,
  Table,
  Tag,
} from "antd";
import { UploadOutlined, LeftOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";

const Container = styled.div`
  padding: 24px;
  background-color: #f8f9fa;
  font-family:
    -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const TitleSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  h2 {
    margin: 0;
    font-size: 20px;
    color: #1a1a1a;
  }
`;

const BackButton = styled(Button)`
  background: #f0fdf4;
  color: #15803d;
  border: none;
  &:hover {
    background: #dcfce7 !important;
    color: #166534 !important;
  }
`;

const CalibrateButton = styled(Button)`
  background-color: #16a34a;
  border-color: #16a34a;
  color: white;
  font-weight: 500;
  &:hover {
    background-color: #15803d !important;
    border-color: #15803d !important;
    color: white !important;
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px 32px;
  background: white;
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  margin-bottom: 32px;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.span`
  color: #64748b;
  font-size: 19px;
`;

const Value = styled.span`
  color: #1e293b;
  font-size: 18px;
  font-weight: 500;
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  color: #1e293b;
  margin-bottom: 16px;
`;

const TableWrapper = styled.div`
  background: white;
  padding: 16px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
`;

const CustomUploadArea = styled.div`
  border: 1px dashed #bbf7d0;
  background-color: #f0fdf4;
  border-radius: 20px;
  padding: 10px;
  text-align: center;
  color: #16a34a;
  cursor: pointer;
  font-weight: 500;
  margin-bottom: 24px;
  &:hover {
    background-color: #dcfce7;
  }
`;

export default function SpeedGovernorDashboard() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();

  const [logs, setLogs] = useState([
    {
      key: "1",
      startDate: "20th Feb 2025",
      calibrationDate: "20th Feb 2025",
      certificate: "View",
      provider: "Simba Speed",
    },
    {
      key: "2",
      startDate: "20th Feb 2025",
      calibrationDate: "20th Feb 2025",
      certificate: "View",
      provider: "Simba Speed",
    },
    {
      key: "3",
      startDate: "20th Feb 2025",
      calibrationDate: "20th Feb 2025",
      certificate: "View",
      provider: "Simba Speed",
    },
  ]);

  // Modal Functions
  const showModal = () => setIsModalOpen(true);
  const handleCancel = () => {
    form.resetFields();
    setIsModalOpen(false);
  };

  const handleFinish = (values) => {
    const formattedDate = values.dateCalibrated
      ? values.dateCalibrated.format("DDth MMM YYYY")
      : "-";

    const newLog = {
      key: Date.now().toString(),
      startDate: formattedDate,
      calibrationDate: formattedDate,
      certificate: "View",
      provider: values.provider,
    };

    setLogs([newLog, ...logs]); // Adds new entry to the top of the table
    setIsModalOpen(false);
    form.resetFields();
  };

  // Table Configuration
  const columns = [
    { title: "Start Date", dataIndex: "startDate", key: "startDate" },
    {
      title: "Calibration Dates",
      dataIndex: "calibrationDate",
      key: "calibrationDate",
    },
    {
      title: "Certificate",
      dataIndex: "certificate",
      key: "certificate",
      render: (text) => <a style={{ color: "#6366f1" }}>{text}</a>,
    },
    {
      title: "Speed Governor Provider",
      dataIndex: "provider",
      key: "provider",
    },
  ];
  const navigate = useNavigate();

  return (
    <Container>
      <Header>
        <TitleSection>
          <BackButton
            onClick={() => navigate("/Workshop")}
            shape="circle"
            icon={<LeftOutlined />}
          />
          <h2>Speed Governor - KA12345</h2>
          <Tag color="success">Up to Date</Tag>
        </TitleSection>
        <CalibrateButton type="primary" onClick={showModal}>
          Calibrate
        </CalibrateButton>
      </Header>

      <InfoGrid>
        <InfoItem>
          <Label>Vehicle No</Label>
          <Value>KA12345</Value>
        </InfoItem>
        <InfoItem>
          <Label>Vehicle Type</Label>
          <Value>Truck</Value>
        </InfoItem>
        <InfoItem>
          <Label>Model</Label>
          <Value>Axes 100</Value>
        </InfoItem>
        <InfoItem>
          <Label>Mfg Year</Label>
          <Value>1970</Value>
        </InfoItem>
        <InfoItem>
          <Label>Internal Dimension of truck</Label>
          <Value>20/20/20</Value>
        </InfoItem>
        <InfoItem>
          <Label>Cubic Capacity</Label>
          <Value>10,000</Value>
        </InfoItem>
        <InfoItem>
          <Label>Maximum Load Capacity (in Kg)</Label>
          <Value>20,000</Value>
        </InfoItem>
        <InfoItem>
          <Label>Fuel Type</Label>
          <Value>Diesel</Value>
        </InfoItem>
        <InfoItem>
          <Label>Status</Label>
          <Value style={{ color: "#16a34a" }}>Active</Value>
        </InfoItem>
        <InfoItem>
          <Label>Mileage</Label>
          <Value>80</Value>
        </InfoItem>
        <InfoItem>
          <Label>Registration Date</Label>
          <Value>24th Aug 2024</Value>
        </InfoItem>
        <InfoItem>
          <Label>Transmission</Label>
          <Value>Manual</Value>
        </InfoItem>
        <InfoItem>
          <Label>Last Calibration</Label>
          <Value>20th Feb 2025</Value>
        </InfoItem>
      </InfoGrid>

      {/* Speed Governor Logs Table Section */}
      <SectionTitle>Speed Governor Logs</SectionTitle>
      <TableWrapper>
        <Table
          dataSource={logs}
          columns={columns}
          pagination={{ pageSize: 5 }}
        />
      </TableWrapper>

      <Modal
        title={
          <span style={{ fontSize: "18px", fontWeight: "600" }}>KA123.</span>
        }
        open={isModalOpen}
        onCancel={handleCancel}
        footer={[
          <Button
            key="cancel"
            onClick={handleCancel}
            style={{
              borderRadius: "20px",
              background: "#f0fdf4",
              color: "#16a34a",
              border: "none",
            }}
          >
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={() => form.submit()}
            style={{
              borderRadius: "20px",
              background: "#16a34a",
              borderColor: "#16a34a",
            }}
          >
            Calibrate
          </Button>,
        ]}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          style={{ marginTop: "24px" }}
        >
          <Form.Item
            name="provider"
            label={
              <span style={{ color: "#64748b" }}>Speed Governor Provider</span>
            }
            rules={[{ required: true, message: "Please select a provider" }]}
          >
            <Select placeholder="Select Provider">
              <Select.Option value="Simba Speed">Simba speed</Select.Option>
              <Select.Option value="Delta Governor">
                Delta Governor
              </Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="dateCalibrated"
            label={<span style={{ color: "#64748b" }}>Date Calibrated</span>}
            rules={[
              { required: true, message: "Please select calibration date" },
            ]}
          >
            <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
          </Form.Item>

          <Form.Item name="certificate">
            <Upload beforeUpload={() => false} maxCount={1}>
              <CustomUploadArea>+ Upload Certificate</CustomUploadArea>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>
    </Container>
  );
}
